import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import useTrans from '@hooks/useTrans'
import { api } from '@utils/index'
import { buildFiltersParams, buildSortParams, cn, getQsFilters, getQsSort, getUserType, priceValutaNotation, sortCols, toCamelCase } from '@utils/safeTs'
import { LISTINI_PERSONALIZZAZIONI } from '@utils/endpoint'
import { ListinoPersonalizzazioneType, RootStateType } from '@utils/types'
import { Filter, FiltroRapido } from '@components/atoms'
import LoadingSvg from '@components/atoms/Loading/Loading'
import MyAccountTable, { MyAccountTableSortType } from '@components/atoms/MyAccountTable/MyAccountTable'
import styles from './MyAccountListiniTecnicheList.module.sass'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'

interface Props {
  className?: string
}

type ListinoPriceMode = 'listino_l0' | 'listino_l1' | 'acquisto'
type ListiniFiltersValues = Record<string, any>
type TecnicaFilterOption = {
  label: string
  value: Set<number>
  codiceWeb?: string
}
type ListiniOptionsType = {
  tecnica: Array<{ label: string; value: string }>
  driver: Array<{ label: string; value: string }>
  codicePadre: Array<{ label: string; value: string }>
  codiceFiglio: Array<{ label: string; value: string }>
  numeroColori: Array<{ label: string; value: string }>
  fornitore: Array<{ label: string; value: string }>
}
type ListiniDatasetsByMode = Record<ListinoPriceMode, ListinoPersonalizzazioneType[]>

const quantita = [1, 50, 100, 250, 500, 1000, 2500, 5000, 10000]
const localStorageVisibleColumnsKey = 'listini_tecniche.visiblecolumns'
const priceModes: ListinoPriceMode[] = ['listino_l0', 'listino_l1', 'acquisto']

const createEmptyDatasets = (): ListiniDatasetsByMode => ({
  listino_l0: [],
  listino_l1: [],
  acquisto: [],
})

const normalizeQueryValue = (value) => {
  if (Array.isArray(value)) return [...value].map((item) => String(item)).sort()
  return value == null ? '' : String(value)
}

const areQueriesEqual = (left, right) => {
  const leftKeys = Object.keys(left || {}).sort()
  const rightKeys = Object.keys(right || {}).sort()

  if (leftKeys.length !== rightKeys.length) return false

  return leftKeys.every((key, index) => {
    if (key !== rightKeys[index]) return false
    return JSON.stringify(normalizeQueryValue(left[key])) === JSON.stringify(normalizeQueryValue(right[key]))
  })
}

const sortStrings = (left?: string, right?: string) => {
  if (!left && !right) return 0
  if (!left) return 1
  if (!right) return -1
  return left < right ? -1 : 1
}

const MyAccountListiniTecnicheList = (props: Props) => {
  
  const { 
    className = '',
  } = props

  const t = useTrans()
  const router = useRouter()
  const { utente } = useSelector((state: RootStateType) => state.utente)
  const userType = getUserType(utente)
  const isFornitore = userType === 'fornitore'

  const [loading, setLoading] = useState(true)
  const [priceMode, setPriceMode] = useState<ListinoPriceMode>(isFornitore ? 'acquisto' : 'listino_l0')
  const [datasetsByMode, setDatasetsByMode] = useState<ListiniDatasetsByMode>(createEmptyDatasets)
  const [visibleColKeys, setVisibleColKeys] = useState<string[]>([])
  const [sort, setSort] = useState<MyAccountTableSortType>(getQsSort(router.query.sort))

  const filters = useMemo(() => ({
    tecnica: { label: t('Tecnica') },
    driver: { label: t("Driver") },
    codicePadre: { label: t("Codice padre") },
    codiceFiglio: { label: t("Codice figlio") },
    numeroColori: { label: t("Colori")},
    ...(!isFornitore ? { fornitore: { label: t("Fornitore") } } : {}),
  }), [isFornitore, t])

  const [filtersValues, setFiltersValues] = useState<ListiniFiltersValues>(getQsFilters(router.query, filters))

  useEffect(() => {
    if (isFornitore && priceMode !== 'acquisto') {
      setPriceMode('acquisto')
    }
  }, [isFornitore, priceMode])

  useEffect(() => {
    if (!userType) return

    let isMounted = true

    const loadListini = async () => {
      setLoading(true)

      try {
        if (isFornitore) {
          const response = await api.get(LISTINI_PERSONALIZZAZIONI, {
            params: { price_mode: 'acquisto' },
          })

          if (!isMounted) return

          setDatasetsByMode({
            ...createEmptyDatasets(),
            acquisto: toCamelCase(response.data || []) as ListinoPersonalizzazioneType[],
          })
          return
        }

        const responses = await Promise.all(
          priceModes.map(async (mode) => {
            const response = await api.get(LISTINI_PERSONALIZZAZIONI, {
              params: { price_mode: mode },
            })

            return [mode, toCamelCase(response.data || []) as ListinoPersonalizzazioneType[]] as const
          })
        )

        if (!isMounted) return

        setDatasetsByMode(
          responses.reduce<ListiniDatasetsByMode>((acc, [mode, rows]) => {
            acc[mode] = rows
            return acc
          }, createEmptyDatasets())
        )
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadListini()

    return () => {
      isMounted = false
    }
  }, [isFornitore, userType])

  const priceModeOptions = useMemo(
    () => [
      {
        id: 'listino_l0' as ListinoPriceMode,
        label: t('Prezzi di vendita - L0'),
        color: 'yellow' as const,
        number: datasetsByMode.listino_l0.length,
      },
      {
        id: 'listino_l1' as ListinoPriceMode,
        label: t('Prezzi di vendita - L1'),
        color: 'blue-green' as const,
        number: datasetsByMode.listino_l1.length,
      },
      {
        id: 'acquisto' as ListinoPriceMode,
        label: t('Prezzi di acquisto'),
        color: 'info' as const,
        number: datasetsByMode.acquisto.length,
      },
    ],
    [datasetsByMode, t]
  )

  const currentFilters = useMemo(
    () => (isFornitore || priceMode === 'acquisto' ? filters : _.omit(filters, ['fornitore'])),
    [filters, isFornitore, priceMode]
  )

  const currentRows = useMemo(() => datasetsByMode[priceMode] || [], [datasetsByMode, priceMode])

  const options = useMemo<ListiniOptionsType>(() => {
    const tecniche = Object.values(
      currentRows.reduce<Record<string, TecnicaFilterOption>>((acc, { tecnica }) => {
        if (!tecnica?.nome) return acc

        if (!acc[tecnica.nome]) {
          acc[tecnica.nome] = {
            label: tecnica.nome,
            value: new Set(),
            codiceWeb: tecnica.codiceWeb,
          }
        }

        acc[tecnica.nome].value.add(tecnica.id)
        return acc
      }, {})
    )

    const codicePadre = _.uniq(tecniche.map((item) => item.codiceWeb).filter((item): item is string => !!item)).sort(sortStrings)

    const fornitori = _.uniqBy(
      currentRows.map((item) => item.fornitore).filter(Boolean),
      (item) => item.id
    ).sort((left, right) => sortStrings(left?.ragioneSociale, right?.ragioneSociale))

    return {
      tecnica: tecniche
        .map(({ label, value }) => ({
          label,
          value: Array.from(value).join(','),
        }))
        .sort((left, right) => sortStrings(left.label, right.label)),
      driver: [
        { label: t('Colore'), value: 'C' },
        { label: t('Dimensione'), value: 'M' },
      ],
      codicePadre: codicePadre.map((item) => ({ label: item, value: item })),
      codiceFiglio: _.uniq(currentRows.map((item) => item.codice).filter((item): item is string => !!item))
        .sort(sortStrings)
        .map((item) => ({ label: item, value: item })),
      numeroColori: _.uniq(currentRows
        .filter((item) => item.tipoDriver?.toUpperCase() === 'C')
        .map((item) => item.numeroColoriNome)
        .filter((item): item is string => !!item))
        .sort(sortStrings)
        .map((item) => ({ label: item, value: item })),
      fornitore: fornitori.map((item) => ({
        label: item.ragioneSociale,
        value: item.id?.toString(),
      })),
    }
  }, [currentRows, t])

  const setupValue = (item: ListinoPersonalizzazioneType) =>
    priceMode === 'acquisto' ? item.setupAcquisto : item.setupVendita

  const cols = useMemo<{
    key: string
    label?: string
    render: (item: ListinoPersonalizzazioneType) => React.ReactNode
    style?: string
    getSortValue?: (item: ListinoPersonalizzazioneType) => any
    align?: 'left' | 'right' | 'center'
  }[]>(() => [
    { key: 'tecnica', label: t('Tecnica'), render: (item) => item.tecnica?.nome },
    {
      key: 'driver',
      label: t('Driver'),
      render: (item) => (
        item.tipoDriver === 'C'
          ? t('Colore')
          : item.tipoDriver === 'M'
            ? t('Dimensione')
            : item.tipoDriver
      ),
    },
    { key: 'codice_padre', label: t('Codice padre'), render: (item) => item.tecnica?.codiceWeb },
    { key: 'codice_figlio', label: t('Codice figlio'), render: (item) => item.codice },
    {
      key: 'colori_dim',
      label: t('Colori/Dim. stampa'),
      render: (item) => (item.tipoDriver === 'M' ? item.areaStampa : item.numeroColoriNome),
    },
    ...(priceMode === 'acquisto' && !isFornitore
      ? [{
        key: 'fornitore',
        label: t('Fornitore'),
        render: (item: ListinoPersonalizzazioneType) => item.fornitore?.ragioneSociale,
      }]
      : []),
    {
      key: 'setup',
      label: t('Setup'),
      align: 'right',
      style: 'value',
      getSortValue: (item: ListinoPersonalizzazioneType) => setupValue(item),
      render: (item: ListinoPersonalizzazioneType) => priceValutaNotation(setupValue(item), 2),
    },
    {
      key: 'sottominimo',
      label: t('sottoMinimo'),
      align: 'right',
      style: 'value',
      render: (item: ListinoPersonalizzazioneType) => priceValutaNotation(item.supplementoSottoMinimo, 2),
    },
    ...quantita.map(q => ({ 
      key: q.toString(), 
      label: `${q}+`, 
      align: 'center' as 'left' | 'center' | 'right', 
      style: 'value', 
      getSortValue: (item: ListinoPersonalizzazioneType) => item.prezziScaglioni?.find((p) => p.quantita === q)?.prezzo, 
      render: (item: ListinoPersonalizzazioneType) =>  priceValutaNotation(item.prezziScaglioni?.find((p) => p.quantita === q)?.prezzo),
    }))
  ], [isFornitore, priceMode, t])

  useEffect(() => {
    const defaultVisibleColumns = cols.map((item) => item.key)
    let persistedColumns: string[] = []

    try {
      const parsed = JSON.parse(localStorage.getItem(localStorageVisibleColumnsKey) || '[]')
      persistedColumns = Array.isArray(parsed) ? parsed : []
    } catch {
      persistedColumns = []
    }

    setVisibleColKeys((prev) => {
      const sourceColumns = prev.length ? prev : (persistedColumns.length ? persistedColumns : defaultVisibleColumns)
      let nextColumns = sourceColumns.filter((item) => defaultVisibleColumns.includes(item))

      if (!nextColumns.length) {
        nextColumns = defaultVisibleColumns
      }

      if (
        priceMode === 'acquisto' &&
        !isFornitore &&
        defaultVisibleColumns.includes('fornitore') &&
        !nextColumns.includes('fornitore')
      ) {
        nextColumns = ['fornitore', ...nextColumns]
      }

      return _.isEqual(prev, nextColumns) ? prev : nextColumns
    })
  }, [cols, isFornitore, priceMode])

  useEffect(() => {
    if (visibleColKeys.length) {
      localStorage.setItem(localStorageVisibleColumnsKey, JSON.stringify(visibleColKeys))
    }
  }, [visibleColKeys])

  const visibleColumns = useMemo(() => cols.filter(col => visibleColKeys.includes(col.key)), [cols, visibleColKeys])

  const activeFilterValues = useMemo(
    () => Object.keys(currentFilters).reduce<ListiniFiltersValues>((acc, key) => {
      if (filtersValues[key]) {
        acc[key] = filtersValues[key]
      }
      return acc
    }, {}),
    [currentFilters, filtersValues]
  )

  const activeTecnicaIds = useMemo(() => {
    if (!activeFilterValues.tecnica) return []

    const value = Array.isArray(activeFilterValues.tecnica)
      ? activeFilterValues.tecnica.join(',')
      : activeFilterValues.tecnica
    return value.split(',')
  }, [activeFilterValues.tecnica])

  const filteredResults = useMemo(() => currentRows
    .filter(l => 
      (!activeFilterValues.tecnica || activeTecnicaIds.includes(l.tecnica?.id?.toString())) &&
      (!activeFilterValues.driver || activeFilterValues.driver.toLocaleLowerCase() === l.tipoDriver?.toLocaleLowerCase()) &&
      (!activeFilterValues.codicePadre || activeFilterValues.codicePadre.toLocaleLowerCase() === l.tecnica?.codiceWeb?.toLocaleLowerCase()) &&
      (!activeFilterValues.codiceFiglio || activeFilterValues.codiceFiglio.toLocaleLowerCase() === l.codice?.toLocaleLowerCase()) &&
      (!activeFilterValues.numeroColori || (
        l.tipoDriver?.toUpperCase() === 'C' &&
        activeFilterValues.numeroColori.toLocaleLowerCase() === l.numeroColoriNome?.toLocaleLowerCase()
      )) &&
      (!('fornitore' in currentFilters) || !activeFilterValues.fornitore || activeFilterValues.fornitore === l.fornitore?.id?.toString()) 
    ), 
    [activeFilterValues, activeTecnicaIds, currentFilters, currentRows]
  )

  useEffect(() => {
    const sortParams = buildSortParams(sort)
    const nextQuery = {
      ...(sortParams ? { 'sort[]': sortParams } : {}),
      ...buildFiltersParams(currentFilters, activeFilterValues),
    }

    if (areQueriesEqual(router.query, nextQuery)) return

    router.replace(
      {
        pathname: router.pathname,
        query: nextQuery,
      },
      undefined,
      { shallow: true, scroll: false }
    )
  }, [activeFilterValues, currentFilters, router, sort])

  return (
    <div className={cn(styles.root, className)}>
      {loading ? (
        <LoadingSvg />
      ) : (
        <>
          <div className={styles.filters}>
            <div className={styles.rowFilters}> 
              {Object.keys(currentFilters).map(k => (
                <Filter 
                  key={k}
                  id={`listinitecnichefilters_${k}`}
                  value={filtersValues[k]}
                  onChange={(v) => setFiltersValues(prev => ({...prev, [k]: v }))}
                  options={'options' in currentFilters[k] ? currentFilters[k].options : options?.[k] }
                  {...currentFilters[k]}
                />
              ))}
              <Filter 
                id={"filter_dettagli_tecniche"}
                variant='multiple'
                label={t("Dettagli")} 
                keepLabel
                showReset={false}
                value={visibleColKeys}
                onChange={(v) => setVisibleColKeys(Array.isArray(v) ? v : [])}
                options={cols.map(c => ({ label: c.label, value: c.key }))} 
                dropdownSide='left'
                className={styles.colFilter}
              />
            </div>
            {!isFornitore && (
              <div className={styles.viewModes}>
                {priceModeOptions.map((option) => (
                  <FiltroRapido
                    key={option.id}
                    id={`listini-pricemode-${option.id}`}
                    name="listini-pricemode"
                    label={option.label}
                    number={option.number}
                    color={option.color}
                    selected={priceMode === option.id}
                    className={styles.viewModeButton}
                    onChange={(selected) => {
                      if (selected) setPriceMode(option.id)
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {!!filteredResults.length ? (
            <MyAccountTable 
              headers={visibleColumns.map(c => _.omit(c, ['render', 'style', 'getSortValue']))}
              sort={{
                keys: quantita.map(a => a.toString()),
                activeSort: sort,
                onSort: (s) => setSort(s)
              }}
            >
              {filteredResults
                .sort((a, b) => sortCols(cols, sort, a, b))
                .map(l => (
                  <tr key={l.id}>
                    {visibleColumns.map(c => (
                      <td key={l.id + '_' + c.key} className={cn(styles.cell, styles['cell--' + c.key], 'style' in c ? styles['cell--' + c.style] : '')}>{c.render(l)}</td>
                    ))}
                  </tr>
              ))}
            </MyAccountTable>
          ) : (
            <div className={styles.empty}>
              {t("Nessun risultato")}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MyAccountListiniTecnicheList
